import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Activity, CheckCircle2, ChevronRight, Sparkles, Wrench, XCircle, X } from "lucide-react";
import { api, shortDate } from "../../lib/api";
import { apiError } from "../../lib/auth";
import { NeonButton } from "../common";

const REPAIR = { replay_rejected_whop: "Check & replay rejected Whop events", register_whop_webhook: "Re-register Whop webhook", retry_whop_events: "Replay failed Whop events", storage_retry: "Reconnect storage",
  retranscribe_failed: "Re-run transcriptions", relink_verifications: "Re-link videos to payments", retry_autopilot: "Re-run autopilot",
  retry_dead_letters: "Retry dead-letter jobs", run_diagnostics: "Run diagnostics", resolve_group: "Mark resolved" };
const SEV = { high: "border-[#FF003C] text-[#FF6B8A]", medium: "border-[#FFB800] text-[#FFB800]", resolved: "border-white/20 text-white/40" };

const useRepair = (after) => {
  const [busy, setBusy] = useState("");
  const run = async (action, sig = "") => {
    setBusy(action + sig);
    try { const { data } = await api.post("/admin/errors/repair", { action, sig }); (data.ok ? toast.success : toast.error)(data.detail); after(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  return { busy, run };
};

const Diagnostics = ({ d, run, busy, rerun }) => (
  <section className="panel p-5" data-testid="diag-panel">
    <div className="flex items-center justify-between mb-3">
      <p className="label-xs">System health · {d ? `${d.ok}/${d.total} passing · ${shortDate(d.at)}` : "checking…"}</p>
      <NeonButton variant="ghost" onClick={rerun} data-testid="diag-run-btn"><Activity className="w-3 h-3" /> Run full diagnostics</NeonButton>
    </div>
    <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-2">
      {d?.checks.map((c) => (
        <div key={c.key} className={`border px-3 py-2 ${c.ok ? "border-white/10" : "border-[#FF003C]/50 bg-[#FF003C]/5"}`} data-testid={`diag-${c.key}`}>
          <p className="flex items-center gap-2 text-sm">{c.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-[#00FF66]" /> : <XCircle className="w-3.5 h-3.5 text-[#FF003C]" />}{c.label}</p>
          <p className="text-[11px] text-white/45 mt-0.5">{c.detail}</p>
          {c.repair && <button disabled={!!busy} onClick={() => run(c.repair)} className="font-mono text-[10px] text-[#00E5FF] mt-1 disabled:opacity-40" data-testid={`diag-repair-${c.key}`}><Wrench className="inline w-3 h-3 mr-1" />{REPAIR[c.repair]}</button>}
        </div>
      ))}
    </div>
  </section>
);

const Drawer = ({ sig, close, run, busy }) => {
  const [g, setG] = useState(null);
  const [ai, setAi] = useState("");
  const [thinking, setThinking] = useState(false);
  useEffect(() => { api.get(`/admin/errors/group/${sig}`).then((r) => setG(r.data)).catch((e) => toast.error(apiError(e))); }, [sig]);
  const explain = async () => { setThinking(true); try { setAi((await api.post("/admin/errors/explain", { sig })).data.explanation); } catch (e) { toast.error(apiError(e)); } finally { setThinking(false); } };
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm" onClick={close} data-testid="error-drawer">
      <div className="w-full max-w-2xl h-full overflow-y-auto bg-[#07090C] border-l border-white/10 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start gap-4">
          <div><p className="label-xs">{g?.source} · {g?.where}</p><p className="text-lg font-bold mt-1" data-testid="error-drawer-title">{g?.message || "loading…"}</p></div>
          <button onClick={close} data-testid="error-drawer-close"><X className="w-5 h-5 text-white/50" /></button>
        </div>
        {g && (<>
          <p className="text-xs text-white/45 mt-2">{g.count} occurrence(s) · {g.open} open · first {shortDate(g.first_at)} · last {shortDate(g.last_at)}{g.users.length ? ` · users: ${g.users.join(", ")}` : ""}</p>
          <div className="panel p-4 mt-4"><p className="label-xs mb-1">Diagnosis</p><p className="text-sm text-white/80" data-testid="error-drawer-diagnosis">{g.diagnosis}</p>
            {ai && <p className="text-sm text-[#7DF9FF] mt-3 border-t border-white/10 pt-3" data-testid="error-drawer-ai">{ai}</p>}
            <div className="flex flex-wrap gap-2 mt-3">
              <NeonButton variant="ghost" onClick={explain} disabled={thinking} data-testid="error-drawer-explain"><Sparkles className="w-3 h-3" /> {thinking ? "Guard is analyzing…" : "Explain with AI"}</NeonButton>
              {g.repairs.map((a) => <NeonButton key={a} onClick={() => run(a, sig).then(close)} disabled={!!busy} data-testid={`error-drawer-repair-${a}`}><Wrench className="w-3 h-3" /> {REPAIR[a]}</NeonButton>)}
            </div>
          </div>
          <p className="label-xs mt-6 mb-2">Occurrences</p>
          <ul className="space-y-2" data-testid="error-drawer-occurrences">
            {g.occurrences.map((o, i) => (
              <li key={i} className="border border-white/10 p-3 text-xs" data-testid={`error-occ-${i}`}>
                <p className="font-mono text-white/50">{shortDate(o.at)} · {o.resolved ? "resolved" : "open"}{o.count > 1 ? ` · ×${o.count}` : ""}{o.user ? ` · ${o.user}` : ""}{o.page ? ` · ${o.page}` : ""}</p>
                <p className="text-white/85 mt-1 break-words">{o.where} — {o.message}</p>
                {o.ua && <p className="text-white/35 mt-1 break-words">{o.ua}</p>}
                {o.stack && <pre className="mt-2 text-[10px] text-white/50 whitespace-pre-wrap break-words max-h-40 overflow-y-auto">{o.stack}</pre>}
              </li>
            ))}
          </ul>
        </>)}
      </div>
    </div>
  );
};

export const ErrorCenter = () => {
  const [d, setD] = useState(null);
  const [diag, setDiag] = useState(null);
  const [sel, setSel] = useState(null);
  const [showResolved, setShowResolved] = useState(false);
  const load = useCallback(() => api.get("/admin/errors/groups").then((r) => setD(r.data)), []);
  const rerun = useCallback(() => api.get("/admin/diagnostics").then((r) => setDiag(r.data)), []);
  const { busy, run } = useRepair(() => { load(); rerun(); });
  useEffect(() => { load(); rerun(); }, [load, rerun]);
  const groups = (d?.groups || []).filter((g) => showResolved || g.open);
  return (
    <div className="space-y-6" data-testid="error-center">
      <Diagnostics d={diag} run={run} busy={busy} rerun={rerun} />
      <section className="panel p-5" data-testid="error-groups-panel">
        <div className="flex items-center justify-between mb-3">
          <p className="label-xs">Errors grouped by root cause · 14 days · {d ? `${d.open} open across ${d.kinds} cause(s)` : "loading…"}</p>
          <label className="text-xs text-white/50 flex items-center gap-2"><input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} data-testid="error-show-resolved" />Show resolved</label>
        </div>
        {d && !groups.length && <p className="text-sm text-white/45" data-testid="error-groups-empty">No open errors. Everything is running.</p>}
        <ul className="space-y-2">
          {groups.map((g) => (
            <li key={g.sig} className={`border-l-2 pl-3 py-2 flex items-start gap-3 ${SEV[g.severity].split(" ")[0]}`} data-testid={`error-group-${g.sig}`}>
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setSel(g.sig)}>
                <p className="text-sm text-white break-words">{g.message}</p>
                <p className="font-mono text-[10px] text-white/40 mt-0.5">{g.source} · {g.where || "—"} · {g.open} open / {g.count} total · last {shortDate(g.last_at)}{g.users.length ? ` · ${g.users.length} user(s)` : ""}</p>
                <p className="text-xs text-white/55 mt-1">{g.diagnosis}</p>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                {g.repairs.filter((a) => a !== "resolve_group").slice(0, 1).map((a) => <button key={a} disabled={!!busy} onClick={() => run(a, g.sig)} className="font-mono text-[10px] text-[#00E5FF] disabled:opacity-40" data-testid={`error-repair-${g.sig}`}><Wrench className="inline w-3 h-3 mr-1" />{REPAIR[a]}</button>)}
                {g.open > 0 && <button disabled={!!busy} onClick={() => run("resolve_group", g.sig)} className="font-mono text-[10px] text-white/45 hover:text-white" data-testid={`error-resolve-${g.sig}`}>Mark resolved</button>}
                <button onClick={() => setSel(g.sig)} className="font-mono text-[10px] text-white/45 hover:text-white flex items-center" data-testid={`error-open-${g.sig}`}>Details <ChevronRight className="w-3 h-3" /></button>
              </div>
            </li>
          ))}
        </ul>
      </section>
      {sel && <Drawer sig={sel} close={() => setSel(null)} run={run} busy={busy} />}
    </div>
  );
};
